import { ArrowRightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, Table } from 'antd';
import React from 'react';

import { fetchAPI } from '../../../utils/api';
import { format2dp, formatUTCTimeToLocal } from '../../../utils/format';
import { OrderItemAuditLog } from './types';

// note: backend audits order item edits with table_name="OrderItem" —
// the CLASS name (audit_logs convention, cf. SupplierPaymentLineItem), NOT
// OrderItem.__tablename__ ('order_item'). Keep literal in sync with backend.
export const ORDER_ITEM_AUDIT_TABLE = 'OrderItem';

const TRACKED_COLUMNS = ['quantity', 'price'];

const OrderItemAuditHistory = (props: {
  visible: boolean;
  item: { id: number; name: string } | null;
  onClose: () => void;
}) => {
  const history = useQuery(
    ['order-item-audit-history', props.item?.id],
    async () => {
      const { items } = await fetchAPI('audit-logs', {
        payload: {
          table_name: ORDER_ITEM_AUDIT_TABLE,
          row_id: props.item!.id,
        },
      });
      return ((items || []) as OrderItemAuditLog[]).filter(entry =>
        TRACKED_COLUMNS.includes(entry.column_name)
      );
    },
    // note: fetch only when the drawer is actually open; no retry storm
    // on a transient 5xx — the drawer shows an error + Retry button instead.
    { enabled: !!props.item, retry: false }
  );

  return (
    <Drawer
      visible={props.visible}
      onClose={props.onClose}
      title={
        props.item ? `Change History - ${props.item.name}` : 'Change History'
      }
      width={600}
    >
      {history.isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load change history"
          style={{ marginBottom: 12 }}
        />
      )}
      <Table
        size="small"
        rowKey="id"
        loading={history.isLoading}
        dataSource={history.data || []}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: 'No changes recorded' }}
        columns={[
          {
            title: 'Field',
            dataIndex: 'column_name',
            width: 100,
          },
          {
            title: 'Change',
            key: 'change',
            render: (text, record: OrderItemAuditLog) => (
              <>
                {format2dp(record.old_value) || 'None'} <ArrowRightOutlined />{' '}
                {format2dp(record.new_value)}
              </>
            ),
          },
          {
            title: 'Changed By',
            key: 'changed_by',
            width: 140,
            render: (text, record: OrderItemAuditLog) => (
              <>{(record.created_by || record.user)?.full_name || '-'}</>
            ),
          },
          {
            title: 'When',
            key: 'created_at',
            width: 210,
            render: (text, record: OrderItemAuditLog) => (
              <>
                {formatUTCTimeToLocal(
                  record.created_at,
                  'HH:mm, DD MMM YYYY (Z)'
                )}
              </>
            ),
          },
        ]}
      />
      {history.isError && (
        <Button onClick={() => history.refetch()} style={{ marginTop: 12 }}>
          Retry
        </Button>
      )}
    </Drawer>
  );
};

export default OrderItemAuditHistory;
